import React, { useEffect, useRef, useState } from "react";
import { SaveIcon, ShieldAlert, SquarePen, X } from "lucide-react";

import ButtonV2 from "../../../../src/components/ui/button/v2";
import { NumericInput, toNumericText } from "../../../../src/components/ui/input/NumericInput";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import {
  cancelEditMode,
  selectCaseHeader,
  selectCaseHeaderIsUpdating,
  selectEditableField,
  setEditableField,
  setEditMode,
} from "../logic/Slice";
import { validateExposure } from "../logic/validations";
import { formatIndianGroupedWithDecimals, LAKH } from "../../../lib/utils";

export interface FraudAmountOverviewItemProps {
  field: string;
  onSave: (exposureInRupees: number) => Promise<boolean>;
}

export const lakhsToRupees = (lakhs?: number | null): number | undefined =>
  lakhs === null || lakhs === undefined ? undefined : Math.round(lakhs * LAKH);

export const rupeesToLakhs = (rupees?: number | null): number | undefined =>
  rupees === null || rupees === undefined ? undefined : rupees / LAKH;

const FraudAmountOverviewItem = ({
  field,
  onSave,
}: FraudAmountOverviewItemProps) => {
  const dispatch = useAppDispatch();
  const editableField = useAppSelector(selectEditableField);
  const isUpdating = useAppSelector(selectCaseHeaderIsUpdating);
  const exposure = useAppSelector(selectCaseHeader)?.exposure ?? 0;

  const isEditing = editableField === field;
  const isSaving = isEditing && isUpdating;

  const inputRef = useRef<HTMLInputElement>(null);
  const exposureInLakhs = rupeesToLakhs(exposure) ?? 0;
  const [draftInLakhs, setDraftInLakhs] = useState<number | undefined>(exposureInLakhs);
  const [inputError, setInputError] = useState<string | null>(null);

  const effectiveLakhs = isSaving ? draftInLakhs : exposureInLakhs;
  const readonlyValue = formatIndianGroupedWithDecimals(toNumericText(effectiveLakhs, 1));
  const tooltipValue = formatIndianGroupedWithDecimals(String(exposure));

  useEffect(() => {
    if (!isEditing) return;
    setDraftInLakhs(exposureInLakhs);
    setInputError(null);
    inputRef.current?.focus();
  }, [isEditing, exposureInLakhs]);

  const handleEditClick = () => {
    if (isUpdating) return;
    dispatch(setEditMode(true));
    dispatch(setEditableField(field));
  };

  const handleCancel = () => {
    setDraftInLakhs(exposureInLakhs);
    setInputError(null);
    dispatch(cancelEditMode());
  };

  const handleSave = async () => {
    if (draftInLakhs === exposureInLakhs) {
      dispatch(cancelEditMode());
      return;
    }

    const exposure = lakhsToRupees(draftInLakhs) ?? 0;

    const error = validateExposure(exposure);
    setInputError(error);
    if (error) return;

    const isSuccess = await onSave(exposure);
    if (isSuccess) {
      dispatch(cancelEditMode());
    }
  };

  const showInput = isEditing && !isSaving;

  return (
    <div className="grid gap-3 group grid-cols-[auto_minmax(0,1fr)_auto] items-center">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-4 rounded-md flex items-center justify-center text-gray-500 shrink-0">
          <ShieldAlert />
        </div>
        <div className="text-xs font-medium shrink-0" style={{ color: "#94A3B8", width: 180 }}>
          Fraud Amount (₹ Lakhs)
        </div>
      </div>

      <div className="min-w-0 flex-1">
        {showInput ? (
          <div className="min-w-0">
            <div className="flex items-center justify-between py-1">
              <NumericInput
                ref={inputRef}
                value={draftInLakhs}
                onChange={setDraftInLakhs}
                decimals={1}
                className="w-full flex-grow rounded-[8px] border-0 border-gray-300 pl-1 pr-2 py-0 text-sm font-normal text-gray-900 bg-[#F3F4F6] shadow-none transition-colors focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            {inputError && <p className="mt-1 text-xs text-red-600">{inputError}</p>}
          </div>
        ) : (
          <div className="text-sm text-[#475569] font-medium truncate" title={tooltipValue}>
            {readonlyValue}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 justify-end shrink-0">
        {isSaving ? (
          <span className="text-xs text-gray-500 italic">Saving...</span>
        ) : !isEditing ? (
          <ButtonV2
            variant="ghost"
            className="p-0 w-[28px] h-[28px] bg-slate-200 hover:bg-slate-100 active:bg-slate-300 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={handleEditClick}
            prefix={<SquarePen color="#737373" fill="none" width={16} />}
          />
        ) : (
          <>
            <ButtonV2
              variant="ghost"
              className="p-0 w-[28px] h-[28px] bg-slate-200 hover:bg-slate-100 active:bg-slate-300"
              onClick={handleSave}
              prefix={<SaveIcon color="#737373" fill="none" width={16} />}
            />
            <ButtonV2
              variant="ghost"
              className="p-0 w-[28px] h-[28px] active:bg-slate-300"
              onClick={handleCancel}
              prefix={<X color="#737373" fill="none" width={16} />}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default FraudAmountOverviewItem;
